import { useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsPeopleFill } from 'react-icons/bs';

import { useContactsByListIds, useCompaniesByListIds } from '#contactListFeature/models/queries';
import Alert from '#globalComponents/Alert/Alert';
import Card from '#globalComponents/Card/Card';
import Table from '#globalComponents/Table/Table';

import PostMailingQueryError from '../PostMailingQueryError';

/**
 * Merges person-contact rows and company-only recipients into a single, deduplicated
 * recipient list for display. A list targets two recipient kinds stored in two tables:
 * person contacts (`contactPerson_contactList`) and companies without a contact person
 * (`company_contactList`). Both receive a postal send, so both must appear here.
 *
 * Person rows arrive as join entries ({ crmContacts: {..., crmCompanies} }); company rows
 * arrive as plain CRM company objects. Output is normalized to a common display shape with
 * a stable, collision-free key.
 *
 * A company covered by a person contact is suppressed as a company-only row — this mirrors
 * the send-time dedup (recipientResolver.js), so the count shown here matches what is sent:
 * one send per company, with the contact person taking precedence.
 *
 * @param {Array<Object>} contactRows - Join rows from the contacts endpoint
 * @param {Array<Object>} companies - CRM company objects from the companies endpoint
 * @returns {Array<{key: string, fullName: string|null, company: Object|null}>}
 */
function buildRecipients(contactRows, companies) {
	const seenContacts = new Set();
	const companyIdsWithContact = new Set();
	const result = [];

	for (const row of contactRows) {
		const contact = row.crmContacts;
		if (!contact || seenContacts.has(contact.id)) continue;
		seenContacts.add(contact.id);
		if (contact.crmCompanies?.id) companyIdsWithContact.add(contact.crmCompanies.id);
		result.push({ key: `contact:${contact.id}`, fullName: contact.fullName, company: contact.crmCompanies });
	}

	for (const company of companies) {
		if (!company || companyIdsWithContact.has(company.id)) continue;
		// company-only recipient: no contact person, so no name to show
		result.push({ key: `company:${company.id}`, fullName: null, company });
	}

	return result;
}

const hasCompleteAddress = (company) =>
	Boolean(company?.addressStreet && company?.addressPostal && company?.addressCity);

const COLUMNS = [
	{
		key: 'fullName',
		label: 'Name',
		render: (recipient) => <span className="text-text">{recipient.fullName || '—'}</span>,
	},
	{
		key: 'companyName',
		label: 'Unternehmen',
		render: (recipient) => <span className="text-text">{recipient.company?.companyName || '-'}</span>,
	},
	{
		key: 'address',
		label: 'Adresse',
		render: (recipient) =>
			hasCompleteAddress(recipient.company) ? (
				<div className="text-text leading-relaxed">
					<div>{recipient.company.addressStreet}</div>
					<div className="text-sm text-bgBase-800">
						{recipient.company.addressPostal} {recipient.company.addressCity}
					</div>
				</div>
			) : (
				<span className="text-sm font-medium text-warning-500">Anschrift unvollständig</span>
			),
	},
];

const PostMailingRecipientsTab = ({ selectedListIds }) => {
	const {
		data: contactRows = [],
		isLoading: loadingContacts,
		isError: contactsError,
		error: contactsErrorObj,
		refetch: refetchContacts,
	} = useContactsByListIds(selectedListIds);
	const {
		data: companyRows = [],
		isLoading: loadingCompanies,
		isError: companiesError,
		error: companiesErrorObj,
		refetch: refetchCompanies,
	} = useCompaniesByListIds(selectedListIds);
	const isLoading = loadingContacts || loadingCompanies;
	const isError = contactsError || companiesError;
	const recipients = useMemo(() => buildRecipients(contactRows, companyRows), [contactRows, companyRows]);

	if (selectedListIds.length === 0) {
		return (
			<Card>
				<div className="text-center py-12">
					<div className="w-14 h-14 bg-bgBase-400 rounded-full flex items-center justify-center mx-auto mb-4">
						<BsPeopleFill className="w-7 h-7 text-bgBase-800" />
					</div>
					<h3 className="text-md font-semibold mb-1">Keine Empfängerlisten ausgewählt</h3>
					<p className="text-sm text-bgBase-800">
						Wählen Sie im Tab „Übersicht“ mindestens eine Empfängerliste aus.
					</p>
				</div>
			</Card>
		);
	}

	return (
		<Card>
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-md font-semibold tracking-tight">Empfänger ({recipients.length})</h3>
			</div>

			<Alert tone="info" className="mb-4">
				Empfänger ohne vollständige Postanschrift werden beim Versand übersprungen.
			</Alert>

			{isError ? (
				<PostMailingQueryError
					error={contactsErrorObj || companiesErrorObj}
					title="Empfänger konnten nicht geladen werden"
					onRetry={() => {
						if (contactsError) refetchContacts();
						if (companiesError) refetchCompanies();
					}}
				/>
			) : (
				<Table
					layoutKey="postRecipients"
					rows={recipients}
					columns={COLUMNS}
					rowId="key"
					isLoading={isLoading}
					emptyLabel="Keine Empfänger in den gewählten Listen."
				/>
			)}
		</Card>
	);
};

PostMailingRecipientsTab.propTypes = {
	selectedListIds: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default PostMailingRecipientsTab;
